import React, { useEffect, useState } from 'react'
import { Alert, Button, Modal, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ProcessedMail, processedMailService } from '../../services/processed-mail.service'
import { toastService } from '../../services/toast.service'
import { MailRule } from '../../types'
import { copyToClipboard } from '../../utils/clipboard'
import { formatDate } from '../../utils/dateFormatter'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { Pagination } from '../common/Pagination'

interface ProcessedMailDialogProps {
  show: boolean
  onHide: () => void
  rule: MailRule | null
}

const ProcessedMailDialog: React.FC<ProcessedMailDialogProps> = ({ show, onHide, rule }) => {
  const { t } = useTranslation()
  const [processedMails, setProcessedMails] = useState<ProcessedMail[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedMailIds, setSelectedMailIds] = useState<Set<number>>(new Set())
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [reloadTrigger, setReloadTrigger] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [selectedError, setSelectedError] = useState<string | null>(null)
  const [errorCopied, setErrorCopied] = useState(false)

  const pageSize = 50

  useEffect(() => {
    const loadProcessedMails = async () => {
      if (!rule || !rule.id) {
        setProcessedMails([])
        return
      }

      setLoading(true)
      setError(null)
      try {
        const response = await processedMailService.listFiltered(page, pageSize, '-processed_at', undefined, rule.id)
        if (response && response.results) {
          setProcessedMails(Array.isArray(response.results) ? response.results : [])
          setTotalPages(Math.ceil((response.count || 0) / pageSize))
        } else {
          setProcessedMails([])
          setTotalPages(1)
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load processed mails')
        setProcessedMails([])
        setTotalPages(1)
      } finally {
        setLoading(false)
      }
    }

    if (show && rule && rule.id) {
      loadProcessedMails()
    } else if (!show) {
      setProcessedMails([])
      setSelectedMailIds(new Set())
      setPage(1)
      setTotalPages(1)
      setError(null)
      setLoading(false)
    }
  }, [show, rule, page, reloadTrigger])

  const toggleSelected = (mailId: number) => {
    const newSelected = new Set(selectedMailIds)
    if (newSelected.has(mailId)) {
      newSelected.delete(mailId)
    } else {
      newSelected.add(mailId)
    }
    setSelectedMailIds(newSelected)
  }

  const toggleAll = () => {
    if (!processedMails || processedMails.length === 0) return
    if (selectedMailIds.size === processedMails.length) {
      setSelectedMailIds(new Set())
    } else {
      setSelectedMailIds(new Set(processedMails.map((mail) => mail.id)))
    }
  }

  const clearSelection = () => {
    setSelectedMailIds(new Set())
  }

  const handleBulkDelete = async () => {
    if (selectedMailIds.size === 0) return

    try {
      await processedMailService.bulkDelete(Array.from(selectedMailIds))
      toastService.showInfo(t('processed_mail_s_deleted', { count: selectedMailIds.size }))
      setSelectedMailIds(new Set())
      setReloadTrigger((prev) => prev + 1)
    } catch (err) {
      setError('Failed to delete processed mails')
    }
  }

  const getStatusIcon = (status: string) => {
    const statusTooltip = (
      <Tooltip id={`status-tooltip-${status}`}>
        <div className="small font-monospace">{status}</div>
      </Tooltip>
    )

    switch (status) {
      case 'SUCCESS':
        return (
          <OverlayTrigger overlay={statusTooltip}>
            <i className="bi bi-check-circle text-success" title="SUCCESS"></i>
          </OverlayTrigger>
        )
      case 'FAILED':
        return (
          <OverlayTrigger overlay={statusTooltip}>
            <i className="bi bi-exclamation-triangle text-danger" title="FAILED"></i>
          </OverlayTrigger>
        )
      default:
        return (
          <OverlayTrigger overlay={statusTooltip}>
            <i className="bi bi-slash-circle text-muted" title={status}></i>
          </OverlayTrigger>
        )
    }
  }

  const truncateError = (error: string | null | undefined, maxLength: number = 20) => {
    if (!error) return ''
    return error.length > maxLength ? error.substring(0, maxLength) + '...' : error
  }

  const handleErrorClick = (error: string) => {
    setSelectedError(error)
    setShowErrorDialog(true)
    setErrorCopied(false)
  }

  const handleCopyError = async () => {
    if (!selectedError) return

    try {
      await copyToClipboard(selectedError)
      setErrorCopied(true)
      toastService.showInfo(t('copied'))
      setTimeout(() => setErrorCopied(false), 2000)
    } catch (err) {
      toastService.showError(t('failed_to_copy_to_clipboard'))
    }
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {t('processed_mail_for')} <em>{rule?.name}</em>
          </Modal.Title>
          <Button variant="link" size="sm" className="text-muted me-auto p-0 p-md-2" title={t('whats_this')} onClick={() => setShowDocsDialog(true)}>
            <i className="bi bi-info-circle"></i>
          </Button>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : !processedMails || processedMails.length === 0 ? (
            <span>{t('no_processed_email_messages_found')}</span>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <div className="form-check m-0 ms-2 me-n2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="all-objects"
                            checked={processedMails && processedMails.length > 0 && selectedMailIds.size === processedMails.length}
                            onChange={toggleAll}
                            disabled={processedMails.length === 0}
                          />
                          <label className="form-check-label" htmlFor="all-objects"></label>
                        </div>
                      </th>
                      <th>{t('subject')}</th>
                      <th>{t('received')}</th>
                      <th>{t('processed')}</th>
                      <th>{t('status')}</th>
                      <th>{t('error')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedMails.map((mail) => (
                      <tr key={mail.id}>
                        <td>
                          <div className="form-check m-0 ms-2 me-n2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`mail-${mail.id}`}
                              checked={selectedMailIds.has(mail.id)}
                              onChange={() => toggleSelected(mail.id)}
                            />
                            <label className="form-check-label" htmlFor={`mail-${mail.id}`}></label>
                          </div>
                        </td>
                        <td>{mail.subject}</td>
                        <td>{formatDate(mail.received)}</td>
                        <td>{formatDate(mail.processed)}</td>
                        <td>{getStatusIcon(mail.status)}</td>
                        <td>
                          {mail.error && (
                            <span
                              className="text-danger"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleErrorClick(mail.error || '')}
                            >
                              {truncateError(mail.error)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="btn-toolbar mt-3">
                <Button variant="outline-secondary" size="sm" className="me-2" onClick={clearSelection} disabled={selectedMailIds.size === 0}>
                  {t('clear')}
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => setShowDeleteConfirm(true)} disabled={selectedMailIds.size === 0}>
                  <i className="bi bi-trash me-1"></i>
                  {t('delete_selected')}
                </Button>
                <div className="ms-auto">
                  <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} maxVisiblePages={5} alwaysVisible={false} />
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        show={showDeleteConfirm}
        title={t('delete_selected')}
        message={t('confirm_delete_selected_items', { count: selectedMailIds.size })}
        onConfirm={() => {
          handleBulkDelete()
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />

      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="processed_mail" />

      <Modal show={showErrorDialog} onHide={() => setShowErrorDialog(false)} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{t('error')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#1e1e1e', padding: '15px', borderRadius: '4px' }}>
            <pre
              style={{
                color: '#d4d4d4',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {selectedError}
            </pre>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleCopyError} disabled={!selectedError}>
            {errorCopied ? (
              <>
                <i className="bi bi-clipboard-check me-1"></i>
                {t('copied')}
              </>
            ) : (
              <>
                <i className="bi bi-clipboard me-1"></i>
                {t('copy_to_clipboard')}
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => setShowErrorDialog(false)}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ProcessedMailDialog
